/**
 * Merko Integrations — error handling.
 *
 * Every backend error is `{ error: { code, message } }`. Branch UI on `code`
 * and show a safe rendition of `message`; the backend may return codes not
 * listed here, which fall through to a generic retry/support state.
 *
 * Two product-level cases are deliberately *not* treated as errors about the
 * user's account: a 404 from a route that was never deployed, and a 503
 * `not_configured`. Both mean the feature is unavailable, not that the user has
 * zero connected accounts.
 */

import type { ApiError } from "./types";

export type IntegrationErrorCode =
    | "not_configured"
    | "unauthorized" | "account_inactive"
    | "unknown_app" | "not_found"
    | "already_connected" | "connection_changed" | "expired_attempt"
    | "verification_required" | "account_mismatch" | "invalid_session"
    | "paused_auth" | "paused_quota"
    | "invalid_source" | "invalid_mapping" | "invalid_profile"
    | "permission_required" | "invalid_frequency"
    | "job_running" | "idempotency_conflict" | "profile_limit"
    | "schema_changed" | "contract_changed" | "provider_failed" | "pagination_loop"
    | "dispatch_failed" | "integration_failed"
    | "writes_disabled" | "unsupported_operation"
    | "preview_mismatch" | "preview_expired" | "target_changed"
    | "profile_reconfigure";

/** How the UI should respond, independent of which screen raised it. */
export type ErrorTreatment =
    /** Product unavailable / administrator setup. Do not offer Connect. */
    | "unavailable"
    /** Send the user back to sign-in or account access. */
    | "reauthenticate"
    /** Stale ID: refresh the catalog or list and drop it. */
    | "refresh"
    /** Restart the connection flow. */
    | "restart_connect"
    /** Stop a callback dead; never replay the session URI. */
    | "abort_callback"
    /** Reconnect the provider; the profile may need re-saving. */
    | "reconnect"
    /** Budget exhausted — stop execution, operator review. */
    | "quota"
    /** Keep the form values and highlight the offending field. */
    | "form"
    /** Prevent a duplicate action and refresh authoritative state. */
    | "conflict"
    /** Broken contract — report to an operator, never silently skip records. */
    | "operator"
    /** Safe to retry, after checking authoritative state. */
    | "retry"
    /** Hide or disable the write UI. */
    | "writes_disabled"
    /** Take a fresh write preview. */
    | "repreview"
    /** Unknown code: generic retry/support. */
    | "generic";

interface Treatment {
    treatment: ErrorTreatment;
    /** What to show when the backend message is unusable. */
    fallback: string;
    /** Which form field the user should be sent back to, when applicable. */
    field?: "source" | "mapping" | "permission" | "frequency" | "group";
}

const TREATMENTS: Record<IntegrationErrorCode, Treatment> = {
    not_configured: {
        treatment: "unavailable",
        fallback: "This integration is not set up yet. An administrator needs to enable it.",
    },
    unauthorized: { treatment: "reauthenticate", fallback: "Please sign in again." },
    account_inactive: { treatment: "reauthenticate", fallback: "This account is not active." },

    unknown_app: { treatment: "refresh", fallback: "That app is no longer available." },
    not_found: { treatment: "refresh", fallback: "That item no longer exists." },

    already_connected: {
        treatment: "refresh",
        fallback: "This app is already connected.",
    },
    connection_changed: {
        treatment: "restart_connect",
        fallback: "The connection changed while this was in progress. Start again.",
    },
    expired_attempt: {
        treatment: "restart_connect",
        fallback: "This connection attempt expired. Start again.",
    },

    verification_required: {
        treatment: "abort_callback",
        fallback: "This connection could not be verified. Start the connection again.",
    },
    account_mismatch: {
        treatment: "abort_callback",
        fallback: "This callback belongs to a different account. Start the connection again.",
    },
    invalid_session: {
        treatment: "abort_callback",
        fallback: "This connection link is no longer valid. Start the connection again.",
    },

    paused_auth: {
        treatment: "reconnect",
        fallback: "The provider connection needs attention. Reconnect, then save the profile again.",
    },
    paused_quota: {
        treatment: "quota",
        fallback: "The import budget is exhausted. An operator needs to review this.",
    },
    profile_reconfigure: {
        treatment: "form",
        fallback: "This profile needs to be reviewed and saved again before it can run.",
    },

    invalid_source: { treatment: "form", fallback: "Check the source details.", field: "source" },
    invalid_mapping: { treatment: "form", fallback: "Check the field mapping.", field: "mapping" },
    invalid_profile: { treatment: "form", fallback: "Check the profile details." },
    permission_required: {
        treatment: "form",
        fallback: "A permission policy is required.",
        field: "permission",
    },
    invalid_frequency: {
        treatment: "form",
        fallback: "Choose a valid sync frequency.",
        field: "frequency",
    },

    job_running: {
        treatment: "conflict",
        fallback: "An import is already running for this profile.",
    },
    idempotency_conflict: {
        treatment: "conflict",
        fallback: "That request key belongs to a different profile.",
    },
    profile_limit: {
        treatment: "conflict",
        fallback: "You have reached the maximum number of import profiles.",
    },

    schema_changed: {
        treatment: "operator",
        fallback: "This integration is unavailable: the provider's data shape changed.",
    },
    contract_changed: {
        treatment: "operator",
        fallback: "This integration is unavailable: its contract changed.",
    },
    provider_failed: {
        treatment: "operator",
        fallback: "The provider could not be reached. This has been flagged for review.",
    },
    pagination_loop: {
        treatment: "operator",
        fallback: "The import stopped to avoid skipping records. An operator needs to review this.",
    },

    dispatch_failed: {
        treatment: "retry",
        fallback: "The request could not be queued. Check the current state, then try again.",
    },
    integration_failed: {
        treatment: "retry",
        fallback: "Something went wrong. Check the current state, then try again.",
    },

    writes_disabled: {
        treatment: "writes_disabled",
        fallback: "Writing to this app is turned off. An administrator needs to enable it.",
    },
    unsupported_operation: {
        treatment: "writes_disabled",
        fallback: "That operation is not supported for this app.",
    },

    preview_mismatch: {
        treatment: "repreview",
        fallback: "The preview no longer matches. Take a fresh preview.",
    },
    preview_expired: {
        treatment: "repreview",
        fallback: "This preview expired. Take a fresh one.",
    },
    target_changed: {
        treatment: "repreview",
        fallback: "The target changed since the preview. Take a fresh one.",
    },
};

export interface IntegrationFailure {
    code: string;
    /** Backend message when usable, otherwise the code's own fallback. */
    message: string;
    treatment: ErrorTreatment;
    field?: Treatment["field"];
    status?: number;
    /** True when the product is unavailable rather than the request wrong. */
    unavailable: boolean;
}

/**
 * A failure this client raised itself, before or instead of a request.
 *
 * Its message is written here, so unlike a provider error it is always safe to
 * show as-is.
 */
export class IntegrationClientError extends Error {
    readonly code: string;
    constructor(code: string, message: string) {
        super(message);
        this.name = "IntegrationClientError";
        this.code = code;
    }
}

/** True for the `{error: {code, message}}` envelope. */
export function isApiError(body: unknown): body is ApiError {
    const error = (body as ApiError | undefined)?.error;
    return !!error && typeof error.code === "string" && typeof error.message === "string";
}

/**
 * Normalise anything thrown by the REST layer into one failure shape.
 *
 * An undeployed route and a `not_configured` 503 both become
 * `treatment: "unavailable"`, so a caller never has to tell "the feature does
 * not exist here" apart from "you have nothing connected".
 */
export function describeFailure(err: any): IntegrationFailure {
    if (err instanceof IntegrationClientError) {
        return { code: err.code, message: err.message, treatment: "generic", unavailable: false };
    }

    const status: number | undefined = err?.response?.status;
    const body = err?.response?.data;

    // No response at all: the request never got an answer.
    if (!err?.response) {
        return {
            code: "network",
            message: "Could not reach the server. Check your connection and try again.",
            treatment: "retry",
            unavailable: false,
        };
    }

    if (isApiError(body)) {
        const code = body.error.code;
        const known = TREATMENTS[code as IntegrationErrorCode];
        const message = body.error.message?.trim() || known?.fallback || genericMessage(status);
        return {
            code,
            message,
            treatment: known?.treatment ?? "generic",
            field: known?.field,
            status,
            unavailable: known?.treatment === "unavailable",
        };
    }

    // A route that the deployment gate never created. Not an account problem.
    if (status === 404) {
        return {
            code: "route_absent",
            message: TREATMENTS.not_configured.fallback,
            treatment: "unavailable",
            status,
            unavailable: true,
        };
    }

    if (status === 503) {
        return {
            code: "not_configured",
            message: TREATMENTS.not_configured.fallback,
            treatment: "unavailable",
            status,
            unavailable: true,
        };
    }

    if (status === 401) {
        return {
            code: "unauthorized",
            message: TREATMENTS.unauthorized.fallback,
            treatment: "reauthenticate",
            status,
            unavailable: false,
        };
    }

    return {
        code: "unknown",
        // Never surface a raw provider error or anything credential-shaped.
        message: genericMessage(status),
        treatment: "generic",
        status,
        unavailable: false,
    };
}

function genericMessage(status?: number): string {
    if (status && status >= 500) return "The service is temporarily unavailable. Try again shortly.";
    return "Something went wrong. Try again, or contact support if it continues.";
}

/** True when the failure means the product is not available in this environment. */
export const isUnavailable = (failure: IntegrationFailure): boolean => failure.unavailable;
